import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import HistoryIcon from '@mui/icons-material/History'
import { modifyUserInventoryService, organizationService } from '../services'
import NegativeSaleMrpSection from '../components/NegativeSaleMrpSection'
import { formatOrganizationLabel } from '../utils/organizationLabel'
import { formatDateTime } from '../utils/dateFormat'
import {
  validatePositiveSellingPriceAndMrp,
} from '../utils/sellingPriceMrp'

const DEFAULT_ROWS = 20
const DEBOUNCE_MS = 300

const formatSourceId = (value) =>
  value != null && value !== '' ? String(value) : '—'

const ModifyUserInventoryPage = () => {
  const navigate = useNavigate()
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [orgInputValue, setOrgInputValue] = useState('')
  const [orgSearchText, setOrgSearchText] = useState('')
  const [orgOptions, setOrgOptions] = useState([])
  const [orgLoading, setOrgLoading] = useState(false)

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS)
  const [total, setTotal] = useState(0)

  const [editOpen, setEditOpen] = useState(false)
  const [editRow, setEditRow] = useState(null)
  const [editValues, setEditValues] = useState({ unit_cost: '', unit_price: '' })
  const [editSaving, setEditSaving] = useState(false)

  const [lineOpen, setLineOpen] = useState(false)
  const [lineItems, setLineItems] = useState([])
  const [linePage, setLinePage] = useState(0)
  const [lineRowsPerPage, setLineRowsPerPage] = useState(DEFAULT_ROWS)
  const [lineTotal, setLineTotal] = useState(0)
  const [lineLoading, setLineLoading] = useState(false)
  const [selectedInventory, setSelectedInventory] = useState(null)
  const [lineEditOpen, setLineEditOpen] = useState(false)
  const [lineEditRow, setLineEditRow] = useState(null)
  const [lineEditValues, setLineEditValues] = useState({ expiry_date: '' })
  const [lineSaving, setLineSaving] = useState(false)

  const [auditOpen, setAuditOpen] = useState(false)
  const [auditRow, setAuditRow] = useState(null)
  const [auditEntries, setAuditEntries] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditPage, setAuditPage] = useState(0)
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(DEFAULT_ROWS)
  const [auditTotal, setAuditTotal] = useState(0)

  const [csvFile, setCsvFile] = useState(null)
  const [csvUploading, setCsvUploading] = useState(false)
  const [csvResult, setCsvResult] = useState(null)
  const [inlineOpen, setInlineOpen] = useState(false)
  const [inlineText, setInlineText] = useState('')
  const [inlineSaving, setInlineSaving] = useState(false)

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        setOrgLoading(true)
        const { items: rows } = await organizationService.getOrganizationsPage({
          page: 1,
          limit: 20,
          search: orgSearchText.trim() || undefined,
        })
        if (!cancelled) {
          setOrgOptions(
            (Array.isArray(rows) ? rows : []).map((org) => ({
              id: String(org.id),
              label: formatOrganizationLabel(org),
            })),
          )
        }
      } catch {
        if (!cancelled) setOrgOptions([])
      } finally {
        if (!cancelled) setOrgLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [orgSearchText])

  const fetchInventory = useCallback(async () => {
    if (!selectedOrg?.id) return
    try {
      setLoading(true)
      setError(null)
      const res = await modifyUserInventoryService.getInventoryList(selectedOrg.id, {
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        sortOrder: 'DESC',
      })
      setItems(res?.items || [])
      setTotal(res?.pagination?.total || 0)
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load user inventory')
    } finally {
      setLoading(false)
    }
  }, [selectedOrg, page, rowsPerPage, search])

  const runSearch = () => {
    setPage(0)
    setSearch(searchInput.trim())
    setSearchLoading(true)
  }

  useEffect(() => {
    if (!loading) setSearchLoading(false)
  }, [loading])

  useEffect(() => {
    fetchInventory()
  }, [fetchInventory])

  const openEditDialog = (row) => {
    setEditRow(row)
    setEditValues({
      unit_cost: row.unitCost ?? '',
      unit_price: row.unitPrice ?? '',
    })
    setEditOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!selectedOrg?.id || !editRow?.inventoryId) return
    const cost = Number(editValues.unit_cost)
    const price = Number(editValues.unit_price)
    const priceError = validatePositiveSellingPriceAndMrp(cost, price, {
      requireBoth: true,
    })
    if (priceError) {
      setToast({
        open: true,
        severity: 'error',
        message: priceError,
      })
      return
    }
    try {
      setEditSaving(true)
      const updated = await modifyUserInventoryService.updateInventoryPrices(
        selectedOrg.id,
        editRow.inventoryId,
        {
          unit_cost: cost,
          unit_price: price,
        },
      )
      setItems((prev) =>
        prev.map((row) =>
          row.inventoryId === editRow.inventoryId
            ? {
                ...row,
                unitCost: updated?.unitCost ?? row.unitCost,
                unitPrice: updated?.unitPrice ?? row.unitPrice,
                dateModified: updated?.dateModified ?? row.dateModified,
              }
            : row,
        ),
      )
      setToast({
        open: true,
        severity: 'success',
        message: 'Inventory price and MRP updated successfully.',
      })
      setEditOpen(false)
    } catch (err) {
      setToast({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to update inventory prices.',
      })
    } finally {
      setEditSaving(false)
    }
  }

  const fetchLineItems = useCallback(async () => {
    if (!selectedOrg?.id || !selectedInventory?.inventoryId) return
    try {
      setLineLoading(true)
      const res = await modifyUserInventoryService.getInventoryLineItems(
        selectedOrg.id,
        selectedInventory.inventoryId,
        {
          page: linePage + 1,
          limit: lineRowsPerPage,
        },
      )
      setLineItems(res?.items || [])
      setLineTotal(res?.pagination?.total || 0)
    } catch (err) {
      setToast({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to fetch inventory line items.',
      })
    } finally {
      setLineLoading(false)
    }
  }, [selectedOrg, selectedInventory, linePage, lineRowsPerPage])

  useEffect(() => {
    if (lineOpen) fetchLineItems()
  }, [lineOpen, fetchLineItems])

  const openLineDialog = (row) => {
    setSelectedInventory(row)
    setLinePage(0)
    setLineRowsPerPage(DEFAULT_ROWS)
    setLineOpen(true)
  }

  const fetchPriceAuditLogs = useCallback(async () => {
    if (!selectedOrg?.id || !auditRow?.inventoryId) return
    try {
      setAuditLoading(true)
      const res = await modifyUserInventoryService.getInventoryPriceAuditLogs(
        selectedOrg.id,
        auditRow.inventoryId,
        {
          page: auditPage + 1,
          limit: auditRowsPerPage,
        },
      )
      setAuditEntries(res?.items || [])
      setAuditTotal(res?.pagination?.total || 0)
    } catch (err) {
      setAuditEntries([])
      setAuditTotal(0)
      setToast({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to load price audit history.',
      })
    } finally {
      setAuditLoading(false)
    }
  }, [selectedOrg, auditRow, auditPage, auditRowsPerPage])

  useEffect(() => {
    if (auditOpen) fetchPriceAuditLogs()
  }, [auditOpen, fetchPriceAuditLogs])

  const openAuditDialog = (row) => {
    setAuditRow(row)
    setAuditPage(0)
    setAuditRowsPerPage(DEFAULT_ROWS)
    setAuditEntries([])
    setAuditTotal(0)
    setAuditOpen(true)
  }

  const closeAuditDialog = () => {
    setAuditOpen(false)
    setAuditRow(null)
    setAuditEntries([])
    setAuditTotal(0)
  }

  const formatAuditAmount = (value) =>
    value == null || Number.isNaN(Number(value)) ? '—' : Number(value).toFixed(2)

  const openLineEditDialog = (row) => {
    setLineEditRow(row)
    setLineEditValues({
      expiry_date: row.expiryDate ? new Date(row.expiryDate).toISOString().slice(0, 10) : '',
    })
    setLineEditOpen(true)
  }

  const handleSaveLineEdit = async () => {
    if (!selectedOrg?.id || !selectedInventory?.inventoryId || !lineEditRow?.id) return
    try {
      setLineSaving(true)
      await modifyUserInventoryService.updateInventoryLineItem(
        selectedOrg.id,
        selectedInventory.inventoryId,
        lineEditRow.id,
        {
          expiry_date: lineEditValues.expiry_date || null,
        },
      )
      setToast({
        open: true,
        severity: 'success',
        message: 'Expiry date updated successfully.',
      })
      setLineEditOpen(false)
      fetchLineItems()
    } catch (err) {
      setToast({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to update expiry date.',
      })
    } finally {
      setLineSaving(false)
    }
  }

  const handleExportCsv = async () => {
    if (!selectedOrg?.id) return
    try {
      const res = await modifyUserInventoryService.exportInventoryCsv(selectedOrg.id, {
        search: search || undefined,
      })
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `modify-user-inventory-org-${selectedOrg.id}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setToast({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to export inventory CSV.',
      })
    }
  }

  const handleUploadCsv = async () => {
    if (!selectedOrg?.id || !csvFile) return
    try {
      setCsvUploading(true)
      const res = await modifyUserInventoryService.importBulkPricesCsv(selectedOrg.id, csvFile)
      setCsvResult(res)
      setToast({
        open: true,
        severity: 'success',
        message: 'Bulk CSV import completed.',
      })
      fetchInventory()
    } catch (err) {
      setToast({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to import bulk CSV.',
      })
    } finally {
      setCsvUploading(false)
    }
  }

  const inlineRowsPreview = useMemo(
    () => inlineText.split('\n').map((line) => line.trim()).filter(Boolean),
    [inlineText],
  )

  const handleInlineBulkUpdate = async () => {
    if (!selectedOrg?.id) return
    try {
      setInlineSaving(true)
      const updates = inlineRowsPreview.map((line, idx) => {
        const [inventoryIdRaw, unitCostRaw, unitPriceRaw] = line.split(',').map((v) => v.trim())
        const update = { inventoryId: Number(inventoryIdRaw) }
        if (unitCostRaw !== '') update.unit_cost = Number(unitCostRaw)
        if (unitPriceRaw !== '') update.unit_price = Number(unitPriceRaw)
        if (!Number.isFinite(update.inventoryId) || update.inventoryId <= 0) {
          throw new Error(`Invalid inventoryId in line ${idx + 1}`)
        }
        if (
          update.unit_cost != null &&
          update.unit_price != null
        ) {
          const inlinePriceError = validatePositiveSellingPriceAndMrp(
            update.unit_cost,
            update.unit_price,
            { requireBoth: true },
          )
          if (inlinePriceError) {
            throw new Error(`Line ${idx + 1}: ${inlinePriceError}`)
          }
        } else if (update.unit_cost != null && !(Number(update.unit_cost) > 0)) {
          throw new Error(`Line ${idx + 1}: Selling Price must be greater than 0`)
        } else if (update.unit_price != null && !(Number(update.unit_price) > 0)) {
          throw new Error(`Line ${idx + 1}: MRP must be greater than 0`)
        }
        return update
      })
      const res = await modifyUserInventoryService.inlineBulkUpdatePrices(selectedOrg.id, updates)
      setToast({
        open: true,
        severity: 'success',
        message: `Inline bulk update done. Updated: ${res.updated}, Skipped: ${res.skipped}.`,
      })
      setInlineOpen(false)
      setInlineText('')
      fetchInventory()
    } catch (err) {
      setToast({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || err?.message || 'Inline bulk update failed.',
      })
    } finally {
      setInlineSaving(false)
    }
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
        Modify User Inventory
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        This page updates prices on existing inventory lots and requires{' '}
        <strong>inventory_id</strong>. To add new stock by SKU, use{' '}
        <Button
          size="small"
          variant="text"
          sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline', textTransform: 'none' }}
          onClick={() => navigate('/inventory-sync/create')}
        >
          Create Inventory Sync
        </Button>
        .
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Autocomplete
                fullWidth
                loading={orgLoading}
                options={orgOptions}
                filterOptions={(options) => options}
                value={selectedOrg}
                inputValue={orgInputValue}
                getOptionLabel={(option) => option?.label || ''}
                onChange={(_, option) => {
                  setSelectedOrg(option)
                  setOrgInputValue(option?.label || '')
                  setPage(0)
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setOrgInputValue(value || '')
                    setOrgSearchText(value || '')
                  } else if (reason === 'reset') {
                    setOrgInputValue(value || '')
                  }
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Shop Id (required)" size="small" />
                )}
              />
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Article code / product name"
              />
              <Button
                variant="contained"
                onClick={runSearch}
                disabled={!selectedOrg || searchLoading}
                sx={{ minWidth: 90 }}
              >
                {searchLoading ? <CircularProgress size={18} color="inherit" /> : 'Apply'}
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={!selectedOrg}
                onClick={handleExportCsv}
              >
                Download CSV
              </Button>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={!selectedOrg || csvUploading}
              >
                {csvFile ? csvFile.name : 'Select Bulk CSV'}
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Button
                variant="contained"
                disabled={!selectedOrg || !csvFile || csvUploading}
                onClick={handleUploadCsv}
              >
                {csvUploading ? 'Uploading...' : 'Upload CSV'}
              </Button>
              <Button
                variant="contained"
                disabled={!selectedOrg}
                onClick={() => setInlineOpen(true)}
              >
                Inline Bulk Update
              </Button>
            </Stack>
            {csvResult && (
              <Alert severity="info">
                Processed: {csvResult.processed || 0}, Updated: {csvResult.updated || 0}, Skipped:{' '}
                {csvResult.skipped || 0}, Errors: {csvResult.errors?.length || 0}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      {!selectedOrg && <Alert severity="info">Select a shop first to view inventory.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {selectedOrg && (
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                  Total records: {total}
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Inventory ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Article Code</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Selling Price</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>MRP</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>PO ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Sync ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Non-Shwapno</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((row) => (
                        <TableRow key={row.inventoryId} hover>
                          <TableCell>{row.inventoryId}</TableCell>
                          <TableCell>{row.articleCode || 'N/A'}</TableCell>
                          <TableCell>{row.productName || 'N/A'}</TableCell>
                          <TableCell>{row.quantityOnHand}</TableCell>
                          <TableCell>{row.unitCost == null ? 'N/A' : Number(row.unitCost).toFixed(2)}</TableCell>
                          <TableCell>{row.unitPrice == null ? 'N/A' : Number(row.unitPrice).toFixed(2)}</TableCell>
                          <TableCell>{formatSourceId(row.purchaseOrderId)}</TableCell>
                          <TableCell>{formatSourceId(row.inventorySyncId)}</TableCell>
                          <TableCell>
                            {row.isNonShwapno ? (
                              <Chip size="small" label="Non-Shwapno" color="warning" />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => openEditDialog(row)}
                              >
                                Edit Price/MRP
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<HistoryIcon />}
                                onClick={() => openAuditDialog(row)}
                              >
                                Price History
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => openLineDialog(row)}
                              >
                                Lots
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[20, 50, 100]}
                  onPageChange={(_, nextPage) => setPage(nextPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10))
                    setPage(0)
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {selectedOrg?.id && <NegativeSaleMrpSection orgId={selectedOrg.id} />}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Inventory Price and MRP</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Selling Price"
              type="number"
              size="small"
              value={editValues.unit_cost}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, unit_cost: e.target.value }))
              }
            />
            <TextField
              label="MRP"
              type="number"
              size="small"
              value={editValues.unit_price}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, unit_price: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={auditOpen} onClose={closeAuditDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Price / MRP History — {auditRow?.productName || auditRow?.articleCode || '—'}
        </DialogTitle>
        <DialogContent>
          {auditLoading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : auditEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No price or MRP changes recorded for this inventory lot yet.
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Date &amp; Time</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Shop</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Prev. Selling Price
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        New Selling Price
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Prev. MRP
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        New MRP
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Changed By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(entry.changedAt)}
                        </TableCell>
                        <TableCell>{entry.orgName || selectedOrg?.label || '—'}</TableCell>
                        <TableCell>
                          {entry.productName || entry.articleCode || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {formatAuditAmount(entry.previousUnitCost)}
                        </TableCell>
                        <TableCell align="right">
                          {formatAuditAmount(entry.newUnitCost)}
                        </TableCell>
                        <TableCell align="right">
                          {formatAuditAmount(entry.previousUnitPrice)}
                        </TableCell>
                        <TableCell align="right">
                          {formatAuditAmount(entry.newUnitPrice)}
                        </TableCell>
                        <TableCell>
                          {entry.changedByEmail || entry.changedBy || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={auditTotal}
                page={auditPage}
                rowsPerPage={auditRowsPerPage}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={(_, nextPage) => setAuditPage(nextPage)}
                onRowsPerPageChange={(e) => {
                  setAuditRowsPerPage(parseInt(e.target.value, 10))
                  setAuditPage(0)
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAuditDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lineOpen} onClose={() => setLineOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Inventory Line Items ({selectedInventory?.productName || '-'})
        </DialogTitle>
        <DialogContent>
          {lineLoading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Line Item ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Article Code</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Stock Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total Stock</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>PO ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Sync ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Expiry Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.articleCode || 'N/A'}</TableCell>
                        <TableCell>{row.productName || 'N/A'}</TableCell>
                        <TableCell>{row.stockQty}</TableCell>
                        <TableCell>{row.totalStock}</TableCell>
                        <TableCell>{formatSourceId(row.purchaseOrderId)}</TableCell>
                        <TableCell>{formatSourceId(row.inventorySyncId)}</TableCell>
                        <TableCell>{row.expiryDate || 'N/A'}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => openLineEditDialog(row)}
                          >
                            Edit Expiry
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={lineTotal}
                page={linePage}
                rowsPerPage={lineRowsPerPage}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={(_, nextPage) => setLinePage(nextPage)}
                onRowsPerPageChange={(e) => {
                  setLineRowsPerPage(parseInt(e.target.value, 10))
                  setLinePage(0)
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={lineEditOpen} onClose={() => setLineEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Expiry Date</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Expiry Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={lineEditValues.expiry_date}
              onChange={(e) =>
                setLineEditValues((s) => ({ ...s, expiry_date: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLineEdit} disabled={lineSaving}>
            {lineSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inlineOpen} onClose={() => setInlineOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Inline Bulk Update</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
            Paste one update per line using: inventory_id, unit_cost, unit_price
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={8}
            placeholder="101, 14.5, 16.0"
            value={inlineText}
            onChange={(e) => setInlineText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInlineOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInlineBulkUpdate}
            disabled={inlineSaving || inlineRowsPreview.length === 0}
          >
            {inlineSaving ? 'Applying...' : 'Apply Updates'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ModifyUserInventoryPage
